<template>
  <LoadingOverlay :loading="loading" full-height />
  <main class="px-3" v-if="!loading">
    <Header hide-help-button />
    <h1 class="mt-5 text-center">{{ t("LOCATION_PICKER.WHERE_ARE_YOU") }}</h1>
    <div class="radio-group mt-5" id="instore-select-location-type">
      <CustomRadio
        class="mt-3"
        v-for="option in options"
        :key="option.value"
        :value="option.value"
        name="customRadioGroup"
        :id="'instore-select-location-type-' + option.value"
        v-model:model-value="selectedOption"
      >
        <img class="me-2" :src="option.icon" alt="logo" />
        <span>{{ option.label }}</span>
        <div
          v-if="
            selectedOption === 'payjoy_point' && option.value === 'payjoy_point'
          "
        >
          <h2>{{ t("LOCATION_PICKER.HAVE_KIOSK_ID") }}</h2>
          <p>{{ t("LOCATION_PICKER.REQUEST_KIOSK_ID") }}</p>
          <TextInputEnhance
            :label="t('LOCATION_PICKER.INPUT_KIOSK_ID')"
            v-model:value="kioskId"
            :error="errorKioskId"
            :error-icon="errorKioskId"
            :error-message="errorMsg ?? ''"
            @on-enter-key-down="continueBtn"
            id="instore-input-kiosk-id"
          />
        </div>
      </CustomRadio>
    </div>

    <Button
      :disabled="
        !selectedOption || (selectedOption === 'payjoy_point' && !kioskId)
      "
      :click-handler="continueBtn"
      class="mx-auto mt-5"
      label="continue-btn"
      id="instore-submit-kiosk-id-button"
    >
      {{ t("LOCATION_PICKER.CONTINUE") }}
    </Button>
  </main>
  <ChatbotWrapper v-if="!loading" />
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from "vue";
import { setLanguage } from "@/apps/core-app-onboarding/helpers/translations.helper";
import Header from "@/apps/core-app-onboarding/components/Header.vue";
import ChatbotWrapper from "@/apps/core-app-onboarding/components/ChatbotWrapper.vue";
import CustomRadio from "@/apps/core-app-onboarding/components/CustomRadio.vue";
import useAsset from "@/apps/core-app-onboarding/helpers/useAsset";
import TextInputEnhance from "@/shared-components/consumer/TextInputEnhance.vue";
import Button from "@/apps/core-app-onboarding/components/Button.vue";
import { useRoute, useRouter } from "vue-router";
import { APIService } from "@/apps/core-app-onboarding/services/apiClient/api.service";
import { ErrorHandler } from "@/apps/core-app-onboarding/helpers/errorHandlerWithLogger.helper";
import { AndroidInterface } from "@/apps/core-app-onboarding/services/android.service";
import { useI18n } from "@/apps/core-app-onboarding/composables/useI18n";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import { initializeCredolab } from "@/shared-services/credolab-api.service.ts";
import logAnalyticsEvent from "@/apps/core-app-onboarding/services/google-analytics-events.service";
import GoogleAnalyticsEventsHelper from "@/shared-helpers/google-analytics-events.helper";

declare const Android: AndroidInterface;

export default defineComponent({
  name: "LocationPickerView",
  components: {
    LoadingOverlay,
    Button,
    TextInputEnhance,
    CustomRadio,
    Header,
    ChatbotWrapper,
  },
  mounted() {
    this.hideLoader();
    setLanguage(this);
  },
  setup() {
    type locationType = "work_home" | "payjoy_point" | "prior_borrower";
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const selectedOption = ref<locationType>();
    const kioskId = ref<string>();
    const loading = ref<boolean>(false);
    const apiService = new APIService();
    const errorKioskId = ref<boolean>(false);
    const storedSessionId = ref<string>(Android.getCreditLineApplicationId());
    const errorMsg = ref<string | null>();
    const openMarketIsAvailable = ref<boolean>(true);
    const inStoreApi = new APIService();

    onMounted(async () => {
      try {
        const res = await inStoreApi.checkApplicationSource({
          applicationId: storedSessionId.value,
        });
        openMarketIsAvailable.value = res.data.homeOmEnabled;
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      } catch (error: any) {
        ErrorHandler.logAPIError(error, 0);
      }

      initializeCredolab(storedSessionId.value);

      logAnalyticsEvent(
        GoogleAnalyticsEventsHelper.CoreAppOnboarding
          .OMViewLocationPickerScreen,
        "Credit Line Open Market"
      );
    });

    watch(kioskId, () => {
      errorKioskId.value = false;
      errorMsg.value = null;
    });

    const options = [
      {
        value: "work_home",
        label: t("LOCATION_PICKER.WORK_HOME"),
        icon: useAsset("img/core-app-onboarding/location_home.svg"),
      },
      {
        value: "payjoy_point",
        label: t("LOCATION_PICKER.IM_AT_KIOSK"),
        icon: useAsset("img/core-app-onboarding/store.svg"),
      },
      {
        value: "prior_borrower",
        label: t("LOCATION_PICKER.PRIOR_BORROWER"),
        icon: useAsset("img/core-app-onboarding/person_check.svg"),
      },
    ];

    const continueBtn = async () => {
      const applicationId =
        (route.query?.applicationId as string) ??
        Android.getCreditLineApplicationId();

      if (selectedOption.value === "work_home") {
        validateOpenMarketAvailabilityAndNavigate();
      }

      if (selectedOption.value === "payjoy_point") {
        try {
          loading.value = true;
          const res = await apiService.validateKioskId({
            kioskId: kioskId.value as string,
            applicationId: applicationId,
          });

          if (res.data.isKioskValid) {
            validateOpenMarketAvailabilityAndNavigate();
          } else {
            loading.value = false;
            errorKioskId.value = true;
          }
          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        } catch (error: any) {
          console.error(error);
          loading.value = true;
          errorKioskId.value = true;
          errorMsg.value = error.message;
          ErrorHandler.logAPIError(error, 0);
        }
      }

      if (selectedOption.value === "prior_borrower") {
        try {
          loading.value = true;
          await apiService.updateApplicationSource({
            applicationId,
            source: selectedOption.value,
          });
          router.push({
            name: "PersonRegistrationLaunchView",
            query: route.query,
          });
        } catch (error) {
          console.error(error);
          ErrorHandler.logAPIError(error, 0);
        }
      }
    };

    const validateOpenMarketAvailabilityAndNavigate = () => {
      if (openMarketIsAvailable.value) {
        router.push({
          name: "PersonRegistrationLaunchView",
          query: route.query,
        });
      } else {
        router.push({
          name: "ComingSoonView",
        });
      }
    };

    const hideLoader = async () => {
      if (Android?.finishedLoading) {
        Android.finishedLoading();
      }
    };

    return {
      t,
      options,
      errorKioskId,
      errorMsg,
      kioskId,
      loading,
      selectedOption,
      continueBtn,
      hideLoader,
      validateOpenMarketAvailabilityAndNavigate,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

h1 {
  color: $pj-color-green;
  text-align: center;
  font-feature-settings: "liga" off, "clig" off;

  /* SUPER HEADING */
  font-family: Inter;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.8125rem; /* 145% */
}

h2 {
  color: var(--Typography-Text-1, #111);
  font-feature-settings: "liga" off, "clig" off;

  /* Text regular-content-Page title */
  font-family: Inter;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 500;
  line-height: 1.625rem; /* 185.714% */
}

.radio-group {
  display: flex;
  flex-direction: column;
}
</style>
